import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ViewMode, ViewStyleKey, ListViewLayoutMode } from '@/lib/views/types';
import type { AggregationType } from '@/lib/erp/reports';
import type { WidgetLayoutItem } from '@/features/business-dashboard/types';

export type FieldLayoutStyle = 'apple' | 'grid';
export type ExperienceStylePreference = ViewStyleKey | 'auto';
export type TableDensity = 'compact' | 'default' | 'comfortable' | 'spacious';
export type TableTextSize = 'small' | 'medium' | 'large';
export type DetailDensity = 'compact' | 'default' | 'spacious';
export type DetailTextSize = 'small' | 'medium' | 'large';
export type DetailEditMode = 'modal' | 'inline';

/** Report configuration per doctype */
export interface ReportConfig {
  groupByField?: string;
  aggregations: Record<string, AggregationType>;
  visibleColumns: string[];
}

interface PreferencesState {
  fieldLayoutStyle: FieldLayoutStyle;
  setFieldLayoutStyle: (style: FieldLayoutStyle) => void;
  
  // DEC-001: View mode preference
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  
  // DEC-001 V2: Experience view style preference
  experienceStyle: ExperienceStylePreference;
  setExperienceStyle: (style: ExperienceStylePreference) => void;
  
  // DEC-001: Dev mode flag (from env, not persisted)
  // NOTE: This checks Vite's import.meta.env, not process.env
  // devMode: derived from env, not something we toggle in UI
  // setDevMode is currently unused and should not be wired to user controls
  devMode: boolean;
  setDevMode: (enabled: boolean) => void;

  // Dev: Dashboard state override (auto = use real state detection)
  dashboardStateOverride: 'auto' | 'setup' | 'onboarding' | 'active';
  setDashboardStateOverride: (state: 'auto' | 'setup' | 'onboarding' | 'active') => void;

  // Dev: Chatwoot chat card state override (auto = use real data)
  chatwootStateOverride: 'auto' | 'no_account' | 'empty' | 'has_conversations';
  setChatwootStateOverride: (state: 'auto' | 'no_account' | 'empty' | 'has_conversations') => void;

  // Pulse sparkline period (persisted to localStorage)
  pulsePeriod: '7d' | '30d' | '90d' | 'mtd' | 'ytd';
  setPulsePeriod: (period: '7d' | '30d' | '90d' | 'mtd' | 'ytd') => void;

  // List view layout mode per doctype (table vs gallery vs report)
  docTypeViewMode: Record<string, ListViewLayoutMode>;
  setDocTypeViewMode: (doctype: string, mode: ListViewLayoutMode) => void;

  // Report configuration per doctype (group by, aggregations)
  docTypeReportConfig: Record<string, ReportConfig>;
  setDocTypeReportConfig: (doctype: string, config: Partial<ReportConfig>) => void;

  // Advanced table: hidden columns per doctype (stores fieldnames of HIDDEN columns)
  docTypeAdvancedColumns: Record<string, string[]>;
  setDocTypeAdvancedColumns: (doctype: string, hiddenColumns: string[]) => void;

  // Advanced table: row density (global preference)
  tableDensity: TableDensity;
  setTableDensity: (density: TableDensity) => void;

  // Advanced table: text size (global preference)
  tableTextSize: TableTextSize;
  setTableTextSize: (size: TableTextSize) => void;

  // Advanced table: column pinning per doctype
  docTypeColumnPinning: Record<string, { left: string[]; right: string[] }>;
  setDocTypeColumnPinning: (doctype: string, pinning: { left: string[]; right: string[] }) => void;

  // Advanced table: column sizing per doctype (fieldname → width in px)
  docTypeColumnSizing: Record<string, Record<string, number>>;
  setDocTypeColumnSizing: (doctype: string, sizing: Record<string, number>) => void;

  // Advanced table: column order per doctype (ordered array of column ids)
  docTypeColumnOrder: Record<string, string[]>;
  setDocTypeColumnOrder: (doctype: string, order: string[]) => void;

  // Detail page density (global preference)
  detailDensity: DetailDensity;
  setDetailDensity: (density: DetailDensity) => void;

  // Detail page text size (global preference)
  detailTextSize: DetailTextSize;
  setDetailTextSize: (size: DetailTextSize) => void;

  // Detail page editing mode (modal dialog vs inline fields)
  detailEditMode: DetailEditMode;
  setDetailEditMode: (mode: DetailEditMode) => void;

  // Dev: Signup intent override for previewing onboarding pages
  signupIntentOverride: 'auto' | 'chat' | 'selling' | 'pos' | 'crm';
  setSignupIntentOverride: (intent: 'auto' | 'chat' | 'selling' | 'pos' | 'crm') => void;

  // Archive toggle: per-doctype visibility of archived (disabled=1) records
  showArchived: Record<string, boolean>;
  setShowArchived: (doctype: string, value: boolean) => void;

  // Business dashboard layout (Plan 1921)
  businessDashboardLayout: WidgetLayoutItem[];
  setBusinessDashboardLayout: (layout: WidgetLayoutItem[]) => void;
}

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set) => ({
      fieldLayoutStyle: 'apple', // Default to apple style
      setFieldLayoutStyle: (style: FieldLayoutStyle) => {
        set({ fieldLayoutStyle: style });
      },
      
      viewMode: 'experience',
      setViewMode: (mode: ViewMode) => {
        set({ viewMode: mode });
      },
      
      experienceStyle: 'auto',
      setExperienceStyle: (style: ExperienceStylePreference) => {
        set({ experienceStyle: style });
      },
      
      // Dev mode: enabled in development builds, or for allowlisted users (checked at render time)
      devMode: import.meta.env.VITE_DEV_MODE === 'true' || import.meta.env.DEV,
      setDevMode: (enabled: boolean) => {
        set({ devMode: enabled });
      },

      // Dev: Dashboard state override
      dashboardStateOverride: 'auto' as const,
      setDashboardStateOverride: (state: 'auto' | 'setup' | 'onboarding' | 'active') => {
        set({ dashboardStateOverride: state });
      },

      // Dev: Chatwoot chat card state override
      chatwootStateOverride: 'auto' as const,
      setChatwootStateOverride: (state: 'auto' | 'no_account' | 'empty' | 'has_conversations') => {
        set({ chatwootStateOverride: state });
      },

      // Pulse sparkline period
      pulsePeriod: '7d' as const,
      setPulsePeriod: (period: '7d' | '30d' | '90d' | 'mtd' | 'ytd') => {
        set({ pulsePeriod: period });
      },

      // List view layout mode per doctype
      docTypeViewMode: {},
      setDocTypeViewMode: (doctype: string, mode: ListViewLayoutMode) => {
        set((state) => ({
          docTypeViewMode: { ...state.docTypeViewMode, [doctype]: mode },
        }));
      },

      // Advanced table: hidden columns per doctype
      docTypeAdvancedColumns: {},
      setDocTypeAdvancedColumns: (doctype: string, hiddenColumns: string[]) => {
        set((state) => ({
          docTypeAdvancedColumns: {
            ...state.docTypeAdvancedColumns,
            [doctype]: hiddenColumns,
          },
        }));
      },

      // Advanced table: row density (global)
      tableDensity: 'comfortable' as TableDensity,
      setTableDensity: (density: TableDensity) => {
        set({ tableDensity: density });
      },

      // Advanced table: text size (global)
      tableTextSize: 'medium' as TableTextSize,
      setTableTextSize: (size: TableTextSize) => {
        set({ tableTextSize: size });
      },

      // Advanced table: column pinning per doctype
      docTypeColumnPinning: {},
      setDocTypeColumnPinning: (doctype: string, pinning: { left: string[]; right: string[] }) => {
        set((state) => ({
          docTypeColumnPinning: {
            ...state.docTypeColumnPinning,
            [doctype]: pinning,
          },
        }));
      },

      // Advanced table: column sizing per doctype
      docTypeColumnSizing: {},
      setDocTypeColumnSizing: (doctype: string, sizing: Record<string, number>) => {
        set((state) => ({
          docTypeColumnSizing: {
            ...state.docTypeColumnSizing,
            [doctype]: sizing,
          },
        }));
      },

      // Advanced table: column order per doctype
      docTypeColumnOrder: {},
      setDocTypeColumnOrder: (doctype: string, order: string[]) => {
        set((state) => ({
          docTypeColumnOrder: {
            ...state.docTypeColumnOrder,
            [doctype]: order,
          },
        }));
      },

      // Detail page density
      detailDensity: 'default' as DetailDensity,
      setDetailDensity: (density: DetailDensity) => {
        set({ detailDensity: density });
      },

      // Detail page text size
      detailTextSize: 'small' as DetailTextSize,
      setDetailTextSize: (size: DetailTextSize) => {
        set({ detailTextSize: size });
      },

      // Detail page editing mode
      detailEditMode: 'modal' as DetailEditMode,
      setDetailEditMode: (mode: DetailEditMode) => {
        set({ detailEditMode: mode });
      },

      // Dev: Signup intent override
      signupIntentOverride: 'auto' as const,
      setSignupIntentOverride: (intent: 'auto' | 'chat' | 'selling' | 'pos' | 'crm') => {
        set({ signupIntentOverride: intent });
      },

      // Archive toggle: per-doctype
      showArchived: {},
      setShowArchived: (doctype: string, value: boolean) => {
        set((state) => ({
          showArchived: { ...state.showArchived, [doctype]: value },
        }));
      },

      // Business dashboard layout (Plan 1921)
      businessDashboardLayout: [] as WidgetLayoutItem[],
      setBusinessDashboardLayout: (layout: WidgetLayoutItem[]) => {
        set({ businessDashboardLayout: layout });
      },

      // Report configuration per doctype
      docTypeReportConfig: {},
      setDocTypeReportConfig: (doctype: string, config: Partial<ReportConfig>) => {
        set((state) => {
          const existing = state.docTypeReportConfig[doctype] ?? {
            aggregations: {},
            visibleColumns: [],
          };
          return {
            docTypeReportConfig: {
              ...state.docTypeReportConfig,
              [doctype]: {
                ...existing,
                ...config,
              },
            },
          };
        });
      },
    }),
    {
      name: 'frappe-preferences-storage',
      partialize: (state) => ({
        fieldLayoutStyle: state.fieldLayoutStyle,
        viewMode: state.viewMode,
        experienceStyle: state.experienceStyle,
        docTypeViewMode: state.docTypeViewMode,
        docTypeAdvancedColumns: state.docTypeAdvancedColumns,
        docTypeReportConfig: state.docTypeReportConfig,
        tableDensity: state.tableDensity,
        tableTextSize: state.tableTextSize,
        docTypeColumnPinning: state.docTypeColumnPinning,
        docTypeColumnSizing: state.docTypeColumnSizing,
        docTypeColumnOrder: state.docTypeColumnOrder,
        dashboardStateOverride: state.dashboardStateOverride,
        chatwootStateOverride: state.chatwootStateOverride,
        signupIntentOverride: state.signupIntentOverride,
        pulsePeriod: state.pulsePeriod,
        detailDensity: state.detailDensity,
        detailTextSize: state.detailTextSize,
        detailEditMode: state.detailEditMode,
        showArchived: state.showArchived,
        businessDashboardLayout: state.businessDashboardLayout,
        // devMode is NOT persisted - always reads from env
      }),
    }
  )
);

